import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Cake, ChevronLeft, ChevronRight, Megaphone, Sparkles, FileText, Calendar, ArrowRight, Clock, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useFiliale } from '../context/FilialeContext';
import http from '../api/http';
import { API_URL } from '../config/api';

// Palette Talentia (styles inline : aucune config Tailwind à modifier)
const C = {
  navy: '#0B1437', navy2: '#18246B', y: '#FFC93C', teal: '#25B8A8', coral: '#FF7A6B',
  vio: '#8B7CF6', sky: '#4EA8FF', ink: '#0B1437', mut: '#667091', line: '#E2E6F2', bg: '#F1F3FA',
};
const DISPLAY = "'Bricolage Grotesque','DM Sans',system-ui,sans-serif";
const BODY = "'DM Sans',system-ui,-apple-system,'Segoe UI',sans-serif";
const ACCENTS = [C.sky, C.y, C.teal, C.vio, C.coral];
const FONTS = `
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:wght@500;700;800&family=DM+Sans:wght@400;500;700&display=swap');
.tl-focus:focus-visible { outline: 3px solid ${C.y}; outline-offset: 3px; }
@keyframes tl-fade { from { opacity: 0; } to { opacity: 1; } }
.tl-fade { animation: tl-fade .6s ease both; }
@media (prefers-reduced-motion: reduce) { .tl-fade { animation: none; } }
.tl-show { display: grid; gap: 16px; grid-template-columns: 1fr; }
.tl-slide { height: 230px; }
.tl-side { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.tl-tile { aspect-ratio: 1 / 1; }
@media (min-width: 768px) {
  .tl-show { grid-template-columns: 1fr 136px; }
  .tl-slide { height: 288px; }
  .tl-side { grid-template-columns: 1fr; }
  .tl-tile { aspect-ratio: auto; height: 136px; }
}
`;

// Mots du jour : un message différent chaque jour, sans aucune action attendue
const MOTS = [
  "Une bonne journée commence souvent par un simple « bonjour » sincère.",
  "Les grandes équipes se construisent un petit geste d'entraide à la fois.",
  "Prendre cinq minutes pour souffler, c'est aussi une façon de bien travailler.",
  "Ce que vous faites aujourd'hui, quelqu'un à côté de vous en profitera demain.",
  "Un merci glissé au bon moment change l'ambiance d'un open space entier.",
  "La curiosité est le meilleur outil de la semaine : posez une question de plus.",
  "Ici, chaque métier compte. Le vôtre aussi.",
];

const dayOf = (d) => { const x = new Date(d); return isNaN(x) ? null : x.getDate(); };
const formatDateNaissance = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  if (isNaN(x)) return '—';
  return x.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};
const initials = (p) => `${(p.prenom || '')[0] || ''}${(p.nom || '')[0] || ''}`.toUpperCase() || '?';
const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Normalise une création reçue de l'API (les noms de champs peuvent varier)
const toCreation = (c, i) => {
  const raw = c.image_url || c.image || c.image_path || c.visuel || c.url || c.fichier || '';
  const base = http.defaults?.baseURL || '';
  const image = !raw ? '' : /^(https?:|data:)/.test(raw) ? raw : `${base}${raw.startsWith('/') ? '' : '/'}${raw}`;
  return {
    id: c.id ?? i,
    titre: c.titre || c.title || c.nom || 'Création',
    description: c.description || c.legende || c.resume || '',
    categorie: c.categorie || c.type || 'Communication',
    date: c.date_publication || c.date || c.created_at || null,
    image,
    type: c.type || 'slide',
    ordre: c.ordre ?? i,
  };
};

// Compteur animé, une seule fois à l'arrivée
const useCountUp = (target, ms = 1400) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!target) { setN(0); return undefined; }
    if (prefersReducedMotion()) { setN(target); return undefined; }
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / ms);
      setN(Math.round(target * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return n;
};

/* ───────────── Bienvenue ───────────── */
const Welcome = ({ prenom, totalUsers, scope, dateLabel }) => {
  const count = useCountUp(totalUsers);
  const letter = (prenom || 'U')[0].toUpperCase();
  const dots = [
    { a: -20, r: 62, c: C.y, s: 6 }, { a: 40, r: 62, c: C.teal, s: 5 }, { a: 120, r: 62, c: C.coral, s: 6 },
    { a: 200, r: 62, c: C.sky, s: 5 }, { a: 285, r: 62, c: C.vio, s: 6 },
    { a: 10, r: 48, c: C.sky, s: 4 }, { a: 160, r: 48, c: C.y, s: 4 }, { a: 250, r: 48, c: C.teal, s: 4 },
  ];
  return (
    <header
      className="relative grid items-center gap-6 px-7 py-9 overflow-hidden text-white shadow-lg lg:grid-cols-[1fr_auto] lg:px-12 rounded-3xl"
      style={{ background: `linear-gradient(120deg, ${C.navy}, ${C.navy2} 75%, #22318F)` }}
    >
      <div className="relative z-10 max-w-2xl">
        <span className="inline-block px-4 py-1.5 mb-5 text-sm font-bold rounded-full" style={{ background: C.y, color: C.navy }}>
          {scope}
        </span>
        <h1 className="text-4xl leading-tight lg:text-5xl" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.025em' }}>
          Bienvenue, {prenom || 'cher collègue'}
        </h1>
        <p className="mt-4 text-lg leading-relaxed" style={{ color: '#D5DBF7' }}>
          {totalUsers > 0 ? (
            <>
              Nous sommes ravis de vous compter parmi les{' '}
              <b style={{ color: C.y, fontFamily: DISPLAY, fontSize: '1.15em' }}>{count.toLocaleString('fr-FR')}</b>{' '}
              utilisateurs de la plateforme.
            </>
          ) : (
            'Nous sommes ravis de vous compter parmi les utilisateurs de la plateforme.'
          )}
        </p>
        <p className="mt-2 text-sm capitalize" style={{ color: '#A9B4E6' }}>{dateLabel}</p>
      </div>

      {/* Communauté : orbites autour de l'initiale de l'utilisateur */}
      <div className="relative z-10 hidden lg:block" aria-hidden="true">
        <svg viewBox="-80 -80 160 160" width="230" height="230">
          <circle r="70" fill="none" stroke="rgba(255,255,255,.12)" strokeWidth="1" />
          <circle r="56" fill="none" stroke="rgba(255,255,255,.18)" strokeWidth="1" strokeDasharray="2 5" />
          <circle r="34" fill={C.y} />
          <text y="12" textAnchor="middle" style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 34, fill: C.navy }}>{letter}</text>
          {dots.map((d, i) => {
            const rad = (d.a * Math.PI) / 180;
            return <circle key={i} cx={Math.cos(rad) * d.r} cy={Math.sin(rad) * d.r} r={d.s} fill={d.c} />;
          })}
        </svg>
      </div>

      <div className="absolute rounded-full pointer-events-none -right-16 -top-24 h-72 w-72" style={{ background: 'rgba(78,168,255,.12)' }} />
      <div className="absolute rounded-full pointer-events-none -bottom-28 left-1/3 h-64 w-64" style={{ background: 'rgba(139,124,246,.14)' }} />
    </header>
  );
};

/* ───────────── Vitrine Communication ─────────────
   • Grande slide : les 3 premières créations (défilement automatique)
   • À droite : 2 vignettes carrées statiques empilées (créations 4 et 5)
   Formats d'image : slide 1600 × 700 px · vignettes 600 × 600 px */
const Slide = ({ items }) => {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = items.length;

  useEffect(() => {
    if (n < 2 || paused || prefersReducedMotion()) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 6000);
    return () => clearInterval(t);
  }, [n, paused]);

  if (n === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-8 text-center tl-slide rounded-2xl"
        style={{ background: `linear-gradient(135deg, ${C.bg}, #E8ECFA)`, border: `1px dashed ${C.line}` }}>
        <div className="flex items-end gap-2 mb-3" aria-hidden="true">
          {ACCENTS.map((c, i) => (
            <span key={c} className="rounded-full" style={{ width: 12 + i * 4, height: 12 + i * 4, background: c }} />
          ))}
        </div>
        <p className="text-lg" style={{ fontFamily: DISPLAY, fontWeight: 700 }}>Les créations arrivent bientôt</p>
        <p className="max-w-sm mt-1 text-sm" style={{ color: C.mut }}>
          Affiches, visuels et campagnes de l'équipe Communication seront présentés ici.
        </p>
      </div>
    );
  }

  const cur = items[idx];
  const go = (d) => setIdx((i) => (i + d + n) % n);
  const arrow = { background: 'rgba(11,20,55,.55)', backdropFilter: 'blur(6px)' };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };

  return (
    <div className="relative overflow-hidden text-white tl-slide rounded-2xl"
      style={{ background: `linear-gradient(135deg, ${C.navy2}, ${C.vio})` }}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
      <div key={cur.id} className="absolute inset-0 tl-fade">
        {cur.image && <img src={getImageUrl(cur.image)} alt={cur.titre} className="object-cover w-full h-full" />}
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(11,20,55,0) 40%, rgba(11,20,55,.85))' }} />

      <div className="absolute inset-x-0 bottom-0 p-5 pr-24">
        <span className="inline-block px-3 py-1 mb-2 text-xs font-bold rounded-full" style={{ background: C.y, color: C.navy }}>{cur.categorie}</span>
        <h4 className="text-xl leading-snug truncate" style={{ fontFamily: DISPLAY, fontWeight: 800, letterSpacing: '-.02em' }}>{cur.titre}</h4>
        {cur.description && <p className="mt-0.5 text-sm truncate" style={{ color: '#D5DBF7' }}>{cur.description}</p>}
      </div>

      {n > 1 && (
        <>
          <button type="button" onClick={() => go(-1)} aria-label="Création précédente"
            className="absolute flex items-center justify-center text-white -translate-y-1/2 rounded-full w-9 h-9 tl-focus left-3 top-1/2" style={arrow}>
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button type="button" onClick={() => go(1)} aria-label="Création suivante"
            className="absolute flex items-center justify-center text-white -translate-y-1/2 rounded-full w-9 h-9 tl-focus right-3 top-1/2" style={arrow}>
            <ChevronRight className="w-5 h-5" />
          </button>
          <div className="absolute flex items-center gap-1.5 right-5 bottom-6" role="tablist" aria-label="Choisir une création">
            {items.map((it, i) => (
              <button key={it.id} type="button" role="tab" aria-selected={i === idx} aria-label={`Afficher : ${it.titre}`}
                onClick={() => setIdx(i)} className="h-2 rounded-full tl-focus"
                style={{ width: i === idx ? 22 : 8, background: i === idx ? C.y : 'rgba(255,255,255,.45)', transition: 'width .25s' }} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Tile = ({ item, color }) => {
  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${API_URL}${image}`;
  };
  
  return (
    <figure className="relative m-0 overflow-hidden tl-tile rounded-2xl"
      style={{ background: item ? `linear-gradient(135deg, ${color}, ${C.navy2})` : C.bg, border: item ? 'none' : `1px dashed ${C.line}` }}>
      {item?.image && <img src={getImageUrl(item.image)} alt={item.titre} className="absolute inset-0 object-cover w-full h-full" />}
      {item && (
        <>
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(11,20,55,0) 55%, rgba(11,20,55,.85))' }} />
          <figcaption className="absolute inset-x-0 bottom-0 px-3 pb-2 text-xs font-bold leading-tight text-white truncate">{item.titre}</figcaption>
        </>
      )}
    </figure>
  );
};

const Showcase = ({ items }) => {
  const sorted = [...items].sort((a, b) => a.ordre - b.ordre);
  const slides = sorted.filter((i) => i.type === 'slide').slice(0, 3);
  const tiles = sorted.filter((i) => i.type === 'vignette').slice(0, 2);
  const showSide = tiles.length > 0;
  return (
    <div className="tl-show" style={showSide ? undefined : { gridTemplateColumns: '1fr' }}>
      <Slide items={slides} />
      {showSide && (
        <div className="tl-side">
          {[0, 1].map((i) => <Tile key={i} item={tiles[i]} color={ACCENTS[(i + 2) % ACCENTS.length]} />)}
        </div>
      )}
    </div>
  );
};

/* ───────────── Annonces Écrites ───────────── */
const AnnouncementCard = ({ announcement, color, index }) => {
  const accent = { urgent: C.coral, important: C.y }[announcement.priorite] || C.sky;
  const accentText = announcement.priorite === 'important' ? '#8A6100' : accent;
  const dateStr = (announcement.date || announcement.date_publication || announcement.created_at)
    ? new Date(announcement.date || announcement.date_publication || announcement.created_at)
        .toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';

  return (
    <article className="relative group overflow-hidden rounded-2xl tl-fade transition-all duration-300 hover:shadow-lg"
      style={{ background: '#fff', border: `1px solid ${C.line}`, animationDelay: `${index * 100}ms` }}>
      <div className="absolute top-0 left-0 w-1 h-full transition-all duration-300 group-hover:h-full"
        style={{ background: accent }} />
      <div className="p-5 pl-6">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <span className="inline-block px-2 py-1 mb-2 text-xs font-bold rounded-md"
              style={{ background: `${accent}20`, color: accentText }}>
              {announcement.categorie || 'Annonce'}
            </span>
            <h4 className="text-base font-bold leading-tight" style={{ fontFamily: DISPLAY, fontWeight: 700, color: C.navy }}>
              {announcement.titre}
            </h4>
          </div>
          {announcement.priorite === 'urgent' && (
            <span className="flex-shrink-0 w-2 h-2 rounded-full animate-pulse" style={{ background: C.coral }} />
          )}
        </div>
        <p className="text-sm leading-relaxed line-clamp-3 whitespace-pre-line" style={{ color: C.mut }}>
          {announcement.contenu || announcement.description || announcement.message}
        </p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: C.line }}>
          <div className="flex items-center gap-2 text-xs" style={{ color: C.mut }}>
            <Calendar className="w-3 h-3" />
            <span>{dateStr}</span>
          </div>
          {announcement.auteur && (
            <div className="text-xs font-medium" style={{ color: C.vio }}>
              {announcement.auteur}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const Announcements = ({ items }) => {
  const [viewAll, setViewAll] = useState(false);
  const displayItems = viewAll ? items : items.slice(0, 3);
  
  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-8 py-12 rounded-2xl"
        style={{ background: '#fff', border: `1px dashed ${C.line}` }}>
        <FileText className="w-12 h-12 mb-3" style={{ color: C.mut }} />
        <p className="text-sm" style={{ color: C.mut }}>Aucune annonce pour le moment</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {displayItems.map((item, i) => (
          <AnnouncementCard key={item.id || i} announcement={item} color={ACCENTS[i % ACCENTS.length]} index={i} />
        ))}
      </div>
      {items.length > 3 && !viewAll && (
        <button
          onClick={() => setViewAll(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl transition-all hover:shadow-md"
          style={{ background: '#fff', border: `1px solid ${C.line}`, color: C.vio }}
        >
          Voir toutes les annonces ({items.length})
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
      {viewAll && (
        <button
          onClick={() => setViewAll(false)}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl transition-all hover:shadow-md"
          style={{ background: `${C.vio}10`, border: `1px solid ${C.vio}30`, color: C.vio }}
        >
          Réduire
          <ChevronRight className="w-4 h-4 rotate-90" />
        </button>
      )}
    </div>
  );
};

const Card = ({ icon: Icon, title, aside, children, className = '' }) => (
  <section className={`rounded-2xl p-6 shadow-sm ${className}`} style={{ background: '#fff', border: `1px solid ${C.line}` }}>
    <h3 className="flex items-center gap-2 mb-4 text-lg" style={{ fontFamily: DISPLAY, fontWeight: 700, letterSpacing: '-.02em' }}>
      {Icon && <Icon className="w-5 h-5" style={{ color: C.vio }} />}
      {title}
      {aside && <small className="ml-auto text-xs font-normal" style={{ color: C.mut, fontFamily: BODY }}>{aside}</small>}
    </h3>
    {children}
  </section>
);

/* ───────────── Page ───────────── */
const Dashboard = () => {
  const { user } = useAuth();
  const { currentFiliale, isConsolidated } = useFiliale();
  const [totalUsers, setTotalUsers] = useState(0);
  const [birthdays, setBirthdays] = useState([]);
  const [wishedBirthdays, setWishedBirthdays] = useState(new Set());
  const [creations, setCreations] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const alive = useRef(true);

  const sendBirthdayWishes = async (personnelId) => {
    try {
      await http.post('/api/notifications/voeux-anniversaire', { anniversaire_personnel_id: personnelId });
      setWishedBirthdays(prev => new Set([...prev, personnelId]));
    } catch (error) {
      console.error('Erreur envoi vœux:', error);
      if (error.response?.data?.error === 'Vous avez déjà envoyé des vœux aujourd\'hui') {
        alert('Vous avez déjà souhaité un joyeux anniversaire à cette personne aujourd\'hui ! 🎂');
      } else {
        alert('Erreur lors de l\'envoi des vœux');
      }
    }
  };

  useEffect(() => {
    alive.current = true;
    fetchDashboardData();
    return () => { alive.current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConsolidated, currentFiliale]);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(false);
    const params = {};
    if (!isConsolidated && currentFiliale?.id) params.filiale_id = currentFiliale.id;

    // allSettled : si un widget échoue, les autres s'affichent quand même
    const [usersRes, personnelRes, birthdaysRes, creationsRes, announcementsRes] = await Promise.allSettled([
      http.get('/api/users/count'),                                   // ← à adapter à votre API
      http.get('/api/personnel', { params: { ...params, limit: 1 } }),
      http.get('/api/personnel', { params: { ...params, birthday_month: new Date().getMonth() + 1 } }),
      http.get('/api/communication/creations', { params: { ...params, active: true } }), // Créations visuelles
      http.get('/api/communication/annonces', { params: { ...params, limit: 20, active: true } }), // Annonces
    ]);
    if (!alive.current) return;

    const pick = (r) => (r.status === 'fulfilled' ? r.value.data : null);

    const u = pick(usersRes);
    const p = pick(personnelRes);
    setTotalUsers(
      (Array.isArray(u) ? u.length : u?.total ?? u?.count) || p?.total || 0 // repli : effectif si pas d'API utilisateurs
    );

    const b = pick(birthdaysRes);
    const allBirthdays = Array.isArray(b) ? b : b?.data || [];
    // Filtrer pour ne garder que les anniversaires du mois en cours
    const currentMonth = new Date().getMonth() + 1;
    const monthlyBirthdays = allBirthdays.filter(p => {
      if (!p.date_naissance) return false;
      const birthDate = new Date(p.date_naissance);
      return birthDate.getMonth() + 1 === currentMonth;
    });
    setBirthdays(monthlyBirthdays);

    const c = pick(creationsRes);
    const list = Array.isArray(c) ? c : c?.data || c?.items || [];
    setCreations(list.map(toCreation));

    const a = pick(announcementsRes);
    const announcementsList = Array.isArray(a) ? a : a?.data || a?.items || [];
    setAnnouncements(announcementsList);

    setError([usersRes, personnelRes, birthdaysRes, creationsRes, announcementsRes].every((r) => r.status === 'rejected'));
    setLoading(false);
  };

  const wrap = { background: C.bg, color: C.ink, fontFamily: BODY };
  const { mot, isPresidentWord } = useMemo(() => {
    // Chercher une annonce avec la catégorie "Mot du Président"
    const presidentWord = announcements.find(a => a.categorie === 'Mot du Président' && a.active);
    if (presidentWord) {
      return {
        mot: presidentWord.contenu || presidentWord.description || presidentWord.titre,
        isPresidentWord: true
      };
    }
    // Fallback : mot du jour par défaut
    const doy = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    return {
      mot: MOTS[doy % MOTS.length],
      isPresidentWord: false
    };
  }, [announcements]);

  const regularAnnouncements = useMemo(() => {
    return announcements.filter(a => a.categorie !== 'Mot du Président').slice(0, 6);
  }, [announcements]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={wrap}>
        <style>{FONTS}</style>
        <span style={{ color: C.mut }}>Préparation de votre espace…</span>
      </div>
    );
  }

  const today = new Date().getDate();
  const sorted = [...birthdays].sort((a, b) => (dayOf(a.date_naissance) || 99) - (dayOf(b.date_naissance) || 99));
  const scope = isConsolidated ? 'Vue consolidée du groupe' : currentFiliale?.libelle || 'Votre filiale';
  const dateLabel = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const monthLabel = new Date().toLocaleDateString('fr-FR', { month: 'long' });

  return (
    <div className="min-h-screen px-6 py-7 lg:px-9" style={wrap}>
      <style>{FONTS}</style>

      {error && (
        <div className="px-4 py-3 mb-5 text-sm rounded-xl" style={{ background: '#FFE9E6', color: '#9A2B1E' }}>
          Certaines données n'ont pas pu être chargées. Actualisez la page ou réessayez dans un instant.
        </div>
      )}

      <Welcome prenom={user?.prenom} totalUsers={totalUsers} scope={scope} dateLabel={dateLabel} />

      <div className="grid grid-cols-12 gap-5 mt-5">
        <Card icon={Megaphone} title="À la une de la Communication" aside={creations.length > 0 ? `${creations.length} création${creations.length > 1 ? 's' : ''}` : undefined}
          className="col-span-12 xl:col-span-8">
          <Showcase items={creations} />
        </Card>

        <Card icon={Cake} title="Anniversaires du mois" aside={monthLabel} className="col-span-12 xl:col-span-4">
          {sorted.length === 0 ? (
            <p style={{ color: C.mut }}>Aucun anniversaire ce mois-ci.</p>
          ) : (
            <ul className="grid gap-3 pr-1 overflow-y-auto" style={{ maxHeight: 340 }}>
              {sorted.map((p, i) => {
                const d = dayOf(p.date_naissance);
                const isToday = d === today;
                const hasWished = wishedBirthdays.has(p.idpersonnel || p.id);
                return (
                  <li key={p.id ?? i} className="flex items-center gap-3">
                    <span className="flex items-center justify-center flex-none w-10 h-10 text-sm font-bold rounded-full"
                      style={{ background: ACCENTS[i % ACCENTS.length], color: C.navy }}>{initials(p)}</span>
                    <div className="min-w-0">
                      <div className="font-bold truncate">{p.prenom} {p.nom}</div>
                      <div className="text-xs truncate" style={{ color: C.mut }}>{p.poste || p.fonction || p.departement || 'Collaborateur'}</div>
                      <div className="text-xs" style={{ color: C.mut }}>{formatDateNaissance(p.date_naissance)}</div>
                    </div>
                    <div className="flex items-center gap-2 ml-auto">
                      {isToday && !hasWished && (
                        <button
                          onClick={() => sendBirthdayWishes(p.idpersonnel || p.id)}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-bold rounded-full transition-colors"
                          style={{ background: C.teal, color: '#fff' }}
                          title="Souhaiter un joyeux anniversaire"
                        >
                          <Gift className="w-3 h-3" />
                          Vœux
                        </button>
                      )}
                      {isToday && hasWished && (
                        <span className="px-2 py-1 text-xs font-bold rounded-full"
                          style={{ background: '#ecfdf5', color: '#059669' }}>
                          ✓ Envoyé
                        </span>
                      )}
                      {!isToday && (
                        <span className="px-3 py-1 text-xs font-bold rounded-full whitespace-nowrap"
                          style={{ background: C.bg, color: C.mut }}>
                          {d} {monthLabel}
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card icon={FileText} title="Annonces & Informations" aside={regularAnnouncements.length > 0 ? `${regularAnnouncements.length} annonce${regularAnnouncements.length > 1 ? 's' : ''}` : undefined}
          className="col-span-12">
          <Announcements items={regularAnnouncements} />
        </Card>

        <section className="flex items-center col-span-12 gap-4 px-6 py-5 rounded-2xl"
          style={{ background: '#fff', border: `1px solid ${C.line}`, borderLeft: `6px solid ${isPresidentWord ? C.navy : C.y}` }}>
          {isPresidentWord ? <Megaphone className="flex-none w-6 h-6" style={{ color: C.navy }} /> : <Sparkles className="flex-none w-6 h-6" style={{ color: C.y }} />}
          <div>
            <div className="text-xs font-bold" style={{ color: C.mut }}>{isPresidentWord ? 'Le mot du Président' : 'Le mot du jour'}</div>
            <p className="mt-1 text-lg whitespace-pre-line" style={{ fontFamily: DISPLAY, fontWeight: 500, letterSpacing: '-.01em' }}>{mot}</p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;